/**
 * Approved case-study copy and asset slots.
 * Replace a null screenshot with a same-origin image path when the
 * customer approves that asset.
 * Unverified numbers must stay isDemo: true and verified: false.
 * Set both the other way only after written customer confirmation.
 */

export const DEMO_METRICS_NOTE = 'Demo metrics — pending customer verification';
export const PDF_DEMO_METRICS_NOTE = 'Demo metrics for layout review — pending customer verification.';
export const APPROVAL_DRAFT_NOTE =
  'Draft for customer approval — final logos, visuals and metrics to be replaced after confirmation.';

export function caseStudyMetrics(study) {
  const items = Array.isArray(study?.metrics)
    ? study.metrics.filter((metric) => metric && metric.label && metric.value)
    : [];
  const demo = items.some((metric) => metric.isDemo === true || metric.verified !== true);
  if (process.env.NODE_ENV === 'production' && demo) {
    return { items: [], demo: false };
  }
  return { items, demo: demo && items.length > 0 };
}

const BOOKING_PATH = '/hirewithus/book-demo';

const manestEvidence = [
  'Applicant scoring',
  'First-round AI interviews',
  'Coding assessments',
  'AI follow-up questions',
];

export const caseStudies = {
  manest: {
    slug: 'manest-ai',
    customer: 'manest.ai',
    bookingPath: BOOKING_PATH,
    seo: {
      title: 'manest.ai case study — SearchJob.ai',
      description:
        'How an early-stage AI team turned screening into an evidence-based technical hiring workflow.',
      canonical: 'https://searchjob.ai/case-studies/manest-ai',
      ogImage: 'https://searchjob.ai/og-image.png',
    },
    category: 'AI / Technology / Early-stage',
    focus: 'Technical hiring',
    headline:
      'How an early-stage AI team turned screening into an evidence-based technical hiring workflow.',
    context:
      'SearchJob.ai helped the team evaluate candidates before spending valuable founder and team time in live interviews.',
    assets: {
      logo: '/manest1.png',
      logoClassName: 'h-52 w-auto max-w-none object-contain sm:h-60',
      carouselLogoPlate: 'h-36',
      carouselLogoClass: 'h-64 w-auto max-w-none object-contain',
      proofLogoClassName: 'h-24 w-auto max-w-none object-contain',
      heroScreenshot: null,
      workflowScreenshot: null,
      evidenceScreenshot: null,
    },
    glanceCompact: true,
    glance: [
      ['Customer', 'manest.ai'],
      ['Focus', 'Technical hiring'],
      ['Proof', 'Shortlist in days + customer testimonial'],
    ],
    challenge: {
      title: 'Early-stage AI companies cannot afford a long, noisy screening process.',
      body: "manest.ai's testimonial frames the problem around candidate quality, technical depth and speed.",
      chips: ['Candidate quality', 'Technical depth', 'Speed'],
      snapshot: [
        { label: 'Stage', body: 'Early-stage', icon: 'scale' },
        { label: 'Hiring', body: 'Technical hiring', icon: 'quality' },
        { label: 'Proof', body: 'Shortlist in days + customer quote', icon: 'speed' },
      ],
      oldWorkflow: 'Early-stage AI companies cannot afford a long, noisy screening process.',
    },
    painPoints: ['Candidate quality', 'Technical depth', 'Speed'],
    beforeBalance: [
      { label: 'Candidate quality', body: 'Every hire has to be exceptional.' },
      { label: 'Technical depth', body: 'Technical depth.' },
      { label: 'Speed', body: 'We cannot afford to lose weeks to screening.' },
    ],
    approach: {
      title: 'Multiple evidence points before a live interview.',
      body: 'SearchJob.ai combined applicant scoring, first-round AI interviews and coding assessments with AI follow-up questions. This created multiple evidence points before the founder/team spent time in live interviews.',
      items: manestEvidence,
    },
    workflowTitle: 'Resume to shortlist',
    workflowIntro: 'Applicant scoring, a first-round AI interview and a coding follow-up happen before a live interview.',
    workflow: ['Resume', 'AI interview', 'Coding test', 'AI follow-up', 'Live interview', 'Shortlist'],
    workflowBands: [
      { tone: 'mint', label: 'AI-led workflow', steps: ['Resume', 'AI interview', 'Coding test', 'AI follow-up'] },
      { tone: 'gold', label: 'Human judgment', steps: ['Live interview'] },
      { tone: 'green', label: 'Outcome', steps: ['Shortlist'], checked: true },
    ],
    workflowHighlight: 'AI follow-up',
    workflowNote: {
      label: 'Most differentiated step',
      body: 'The coding follow-up: the system did not stop at whether code was submitted. It asked candidates to explain their own solution.',
    },
    outcomeStory: [
      {
        kind: 'pace',
        icon: 'zap',
        title: 'Faster technical screening',
        body: 'Move from resume to a qualified shortlist without weeks of screening.',
        accent: 'hsl(158 45% 98%)',
        steps: [
          { label: 'Resume', grow: 0.7 },
          { label: 'AI interview', grow: 0.8 },
          { label: 'Coding', grow: 0.9 },
          { label: 'Follow-up', grow: 1 },
        ],
        final: 'Qualified shortlist',
      },
      {
        kind: 'merge',
        icon: 'clock',
        title: 'Evidence before interview',
        body: 'A submitted solution is followed by a question that asks the candidate to explain it.',
        accent: 'hsl(164 32% 97.5%)',
        tasks: ['Applicant scoring', 'AI interview', 'Code submission', 'AI follow-up'],
        hub: 'AI follow-up',
        resultTitle: 'Recruiter review',
        resultMeta: 'Worth meeting',
        resultBadge: 'Evidence',
      },
      {
        kind: 'shortlist',
        icon: 'users',
        title: 'Stronger shortlist',
        body: 'Scored and interviewed profiles are narrowed to the people worth meeting.',
        accent: 'hsl(152 34% 97%)',
        panel: 'Senior shortlist',
        profiles: [
          { initials: 'BE', role: 'Backend engineer', chips: ['Scored', 'Interview'], selected: true },
          { initials: 'ML', role: 'ML engineer', chips: ['Code', 'Follow-up'], selected: true },
          { initials: 'FE', role: 'Frontend engineer', chips: ['Follow-up'], selected: false },
        ],
      },
    ],
    pdfStages: [
      { n: '01', title: 'Candidate quality', body: 'Every hire has to be exceptional' },
      { n: '02', title: 'Technical depth', body: 'Solutions need to be explained' },
      { n: '03', title: 'Speed', body: 'Weeks cannot be lost to screening' },
    ],
    pdfFlow: [
      ['Resume', ''],
      ['AI', 'interview'],
      ['Coding', 'test'],
      ['AI', 'follow-up'],
      ['Live', 'interview'],
      ['Qualified', 'shortlist'],
    ],
    pdfChips: ['Technical depth', 'AI follow-up', 'Evidence-based screening', 'Senior-quality shortlist'],
    heroProofChips: ['Technical depth', 'AI follow-up', 'Stronger shortlist'],
    heroVisualTitle: 'Evidence-based technical screening',
    heroVisualSteps: ['Candidate evidence', 'Coding + AI follow-up', 'Stronger shortlist'],
    heroEvidenceCard: {
      title: 'Candidate evidence',
      profileLabel: 'Technical profile',
      points: ['Skills', 'Experience', 'Role fit'],
    },
    heroFollowUpCard: {
      title: 'Coding + AI follow-up',
      status: 'Code submitted',
      question: 'Explain your approach.',
    },
    heroShortlistCard: {
      title: 'Stronger shortlist',
      rows: [
        { selected: false },
        { selected: true },
        { selected: false },
      ],
    },
    heroProofLine: 'Evidence before the team spends time in live interviews.',
    features: manestEvidence,
    heroVisual: {
      title: 'Technical candidate scorecard',
      description: 'Evidence recorded before a live interview. No manest.ai candidate records are shown.',
      imageKey: 'heroScreenshot',
      items: manestEvidence,
    },
    visuals: {
      heading: 'Demo views of the documented workflow',
      intro: 'Real manest.ai candidate records are not shown. These panels illustrate the workflow in the testimonial.',
      panels: [
        {
          title: 'Code submission with follow-up question',
          imageKey: 'workflowScreenshot',
          blocks: [
            { kicker: 'Code submission', body: 'A solution was submitted.' },
            { kicker: 'Follow-up question', body: 'Explain your own solution.', accent: true },
          ],
        },
        {
          title: 'Candidate evidence report',
          intro: 'Evidence points before the founder/team spent time in a live interview.',
          imageKey: 'evidenceScreenshot',
          items: manestEvidence,
        },
      ],
    },
    metrics: [],
    resultsTitle: 'Customer-backed outcomes',
    resultsIntro: 'The customer received a senior-quality shortlist in days. Scoring, first-round interviews and a coding follow-up were already in place, so the team met only candidates worth meeting.',
    feedbackLine: 'SearchJob.ai handed us a senior-quality shortlist in days.',
    resultEvidence: [
      { label: 'Senior-quality shortlist', body: 'Senior-quality shortlist in days.', kind: 'check' },
      { label: 'Applicant scoring', body: 'SearchJob.ai scored applicants and ran first-round interviews.', kind: 'check' },
      { label: 'Coding follow-up', body: 'Coding tests with AI follow-up identified two candidates who could not explain their own solutions.', kind: 'check' },
    ],
    results: [
      'Senior-quality shortlist in days.',
      'SearchJob.ai scored applicants and ran first-round interviews.',
      'Coding tests with AI follow-up identified two candidates who could not explain their own solutions.',
      'The team spent its time only with candidates worth meeting.',
      'The customer says it closed its lead engineer faster than any role it had filled.',
    ],
    quote:
      'As an early-stage AI team, every hire has to be exceptional and we cannot afford to lose weeks to screening. SearchJob.ai handed us a senior-quality shortlist in days: it scored every applicant, ran the first-round interviews, and its coding tests with AI follow-up flagged two candidates who could not explain their own solutions. We spent our time only with the people worth meeting, and closed our lead engineer faster than any role we have filled.',
    attribution: 'Founder & CEO, manest.ai',
    why: {
      title: 'Stronger evidence before valuable human interview time is spent.',
      body: 'The customer received a senior-quality shortlist in days. Scoring, first-round interviews and a coding follow-up were already in place, so the team met only candidates worth meeting.',
      points: [
        { label: 'Technical depth', body: 'Every hire has to be exceptional, including technical depth.', icon: 'target' },
        { label: 'Evidence before interviews', body: 'Scoring, a first-round interview and a coding follow-up were in place before live interviews.', icon: 'users' },
        { label: 'Founder and team time', body: 'The team spent its time only with candidates worth meeting.', icon: 'heart' },
      ],
    },
  },
  grandGaming: {
    slug: 'grand-gaming',
    heroVariant: 'surge-pipeline',
    heroVisualVariant: 'hiring-surge',
    heroLogo: '/grandgaming.png',
    heroTags: ['Recruitment capacity', 'Candidate matching', 'Candidate communication'],
    heroVisualTitle: 'Hiring surge workflow',
    heroVisualSubtitle: 'From incoming candidates to joining and settling',
    heroIncomingLabel: 'Incoming candidates',
    heroIncomingMore: '+ more candidates',
    pipelineStages: ['Identify', 'Screen', 'Interview', 'Communicate', 'Offer', 'Join', 'Settle'],
    highlightStages: ['Communicate'],
    successStages: ['Join', 'Settle'],
    communicationTitle: 'Continuous candidate communication',
    communicationText: 'Keeping candidates informed through interview, offer, joining and settling.',
    heroInsightCards: [
      {
        title: 'Hiring at scale',
        description: 'Manage high hiring demand with a structured workflow.',
        visual: 'pipeline',
      },
      {
        title: 'Candidate matching',
        description: 'Find candidates aligned with role requirements.',
        visual: 'match',
      },
      {
        title: 'Candidate experience',
        description: 'Stay connected from interview to joining and settling.',
        visual: 'journey',
      },
    ],
    customer: 'Grand Gaming',
    bookingPath: BOOKING_PATH,
    seo: {
      title: 'Grand Gaming case study — SearchJob.ai',
      description: 'When hiring volume spikes, recruitment has to scale without losing candidate experience.',
      canonical: 'https://searchjob.ai/case-studies/grand-gaming',
      ogImage: 'https://searchjob.ai/og-image.png',
    },
    category: 'Gaming / Technology',
    focus: 'Surge hiring',
    headline: 'When hiring volume spikes, recruitment has to scale without losing candidate experience.',
    context:
      'SearchJob.ai supported Grand Gaming with recruitment capacity, technology-enabled matching and candidate communication.',
    assets: {
      logo: '/grandgaming.png',
      logoClassName: 'h-16 w-auto object-contain sm:h-20',
      carouselLogoPlate: 'h-56',
      carouselLogoClass: 'h-48 w-auto max-w-none object-contain',
      proofLogoClassName: '',
      heroScreenshot: null,
      workflowScreenshot: null,
      evidenceScreenshot: null,
    },
    glanceCompact: true,
    glance: [
      ['Customer', 'Grand Gaming'],
      ['Focus', 'Surge hiring'],
      ['Proof', 'Founder & CEO testimonial'],
    ],
    challenge: {
      title: 'When hiring demand increased, the challenge was maintaining delivery quality and candidate experience.',
      body: 'The problem was not simply finding more candidates.',
      chips: ['Not simply finding candidates', 'Delivery quality when demand increases'],
      snapshot: [
        { label: 'Industry', body: 'Gaming / Technology', icon: 'scale' },
        { label: 'Hiring', body: 'Surge hiring', icon: 'speed' },
        { label: 'Proof', body: 'Founder & CEO testimonial', icon: 'quality' },
      ],
      oldWorkflow:
        'The problem is not simply finding candidates. It is maintaining delivery quality when hiring demand increases.',
    },
    painPoints: ['Not simply finding candidates', 'Delivery quality when demand increases'],
    beforeBalance: [
      { label: 'Finding candidates', body: 'The problem is not simply finding candidates.' },
      { label: 'Delivery quality', body: 'It is maintaining delivery quality when hiring demand increases.' },
      { label: 'Candidate experience', body: 'Communication continued through joining and settling into the job.' },
    ],
    approach: {
      title: 'A combined human and technology recruitment operation.',
      body: 'Recruiters handle relationships and decisions. Technology improves matching, screening and workflow throughput.',
      items: [
        'Recruiters handle relationships and decisions',
        'Technology improves matching, screening and workflow throughput',
      ],
    },
    workflowTitle: 'Identify to settle',
    workflowIntro: 'A structured hiring journey that keeps candidates engaged from first contact through joining and settling.',
    workflowStages: [
      { key: 'identify', label: 'Identify', icon: 'search' },
      { key: 'screen', label: 'Screen', icon: 'screen' },
      { key: 'interview', label: 'Interview', icon: 'interview' },
      { key: 'communicate', label: 'Communicate', icon: 'message', emphasis: true },
      { key: 'offer', label: 'Offer', icon: 'offer' },
      { key: 'join', label: 'Join', icon: 'join', success: true },
      { key: 'settle', label: 'Settle', icon: 'settle', success: true },
    ],
    communicationJourney: [
      { key: 'interview', title: 'Interview', caption: 'A great interview experience' },
      { key: 'communicate', title: 'Communicate', caption: 'Timely updates at every step' },
      { key: 'offer', title: 'Offer', caption: 'A clear and simple offer process' },
      { key: 'join', title: 'Join', caption: 'A smooth onboarding experience' },
      { key: 'settle', title: 'Settle', caption: 'Ongoing support to help them settle' },
    ],
    workflow: ['Identify', 'Screen', 'Interview', 'Communicate', 'Offer', 'Join', 'Settle'],
    workflowBands: [
      { tone: 'mint', label: 'Scaled workflow', steps: ['Identify', 'Screen', 'Interview', 'Communicate'] },
      { tone: 'gold', label: 'Human judgment', steps: ['Offer'] },
      { tone: 'green', label: 'Outcome', steps: ['Join & settle'], checked: true },
    ],
    workflowNote: {
      label: 'Candidate communication',
      body: 'Candidate communication continued through joining and settling into the job.',
    },
    outcomeStory: [
      {
        kind: 'pace',
        icon: 'zap',
        title: 'Hiring at scale',
        body: 'Extra hiring demand can move through one structured process.',
        accent: 'hsl(158 45% 98%)',
        steps: [
          { label: 'Demand', grow: 0.7 },
          { label: 'Source', grow: 0.8 },
          { label: 'Screen', grow: 0.9 },
          { label: 'Interview', grow: 1 },
        ],
        final: 'Ready pipeline',
      },
      {
        kind: 'merge',
        icon: 'clock',
        title: 'Candidate matching',
        body: 'Job requirements and candidates are matched before a recruiter spends time.',
        accent: 'hsl(164 32% 97.5%)',
        tasks: ['Open roles', 'Candidate pool', 'Requirements', 'Experience'],
        hub: 'Matching',
        resultTitle: 'Suitable fit',
        resultMeta: 'Job requirements',
        resultBadge: 'Matched',
      },
      {
        kind: 'pace',
        icon: 'users',
        title: 'Candidate experience',
        body: 'Communication continues from screening through joining.',
        accent: 'hsl(152 34% 97%)',
        steps: [
          { label: 'Interview', grow: 0.72 },
          { label: 'Communicate', grow: 0.84 },
          { label: 'Offer', grow: 0.94 },
          { label: 'Join', grow: 1 },
        ],
        final: 'Settle',
      },
    ],
    pdfStages: [
      { n: '01', title: 'Surge hiring', body: 'Hiring demand can spike' },
      { n: '02', title: 'Candidate matching', body: 'Requirements and candidates need to align' },
      { n: '03', title: 'Candidate experience', body: 'Communication continues through joining' },
    ],
    pdfFlow: [
      ['Identify', ''],
      ['Screen', ''],
      ['Interview', ''],
      ['Communicate', ''],
      ['Offer', ''],
      ['Join &', 'settle'],
    ],
    pdfChips: ['Hiring scale', 'Candidate matching', 'Candidate communication', 'White-glove experience'],
    features: ['Candidate matching', 'Screening', 'Workflow throughput', 'Candidate communication'],
    heroVisual: {
      title: 'Hiring pipeline',
      badge: 'Hiring surge',
      description: 'A surge in hiring demand, shown without a volume figure. No Grand Gaming candidate records are shown.',
      imageKey: 'heroScreenshot',
      items: ['Identify', 'Screen', 'Interview', 'Communicate', 'Offer', 'Join', 'Settle'],
    },
    visuals: {
      heading: 'Demo views of the documented workflow',
      intro: 'Real Grand Gaming candidate records are not shown. These panels illustrate the workflow in the testimonial.',
      panels: [
        {
          title: 'Candidate matching',
          intro: 'Technology for matching candidates with job requirements.',
          imageKey: 'workflowScreenshot',
          items: ['Candidate matching', 'Job requirements'],
        },
        {
          title: 'Candidate communication',
          imageKey: 'evidenceScreenshot',
          blocks: [
            {
              kicker: 'Communication',
              body: 'Good communication with the candidate until they join and settle in the job.',
            },
            {
              kicker: 'WhatsApp and voice',
              body: 'Product communication workflow. Not Grand Gaming message records.',
              accent: true,
            },
          ],
        },
      ],
    },
    metrics: [],
    resultsTitle: 'Customer-backed outcomes',
    resultsIntro: 'The public customer statement supports a story around scalability, candidate matching and candidate experience rather than a claim about a specific percentage improvement.',
    feedbackLine: 'The SearchJob team is very competent and well staffed to handle any surge in hiring needs.',
    resultEvidence: [
      { label: 'Surge hiring', body: 'SearchJob.ai was described as well staffed to handle a surge in hiring needs.', kind: 'check' },
      { label: 'Candidate matching', body: 'Technology was highlighted for matching candidates with job requirements.', kind: 'check' },
      { label: 'Candidate communication', body: 'Candidate communication continued through joining and settling into the job.', kind: 'check' },
    ],
    results: [
      'SearchJob.ai was described as well staffed to handle a surge in hiring needs.',
      'Technology was highlighted for matching candidates with job requirements.',
      'Candidate communication continued through joining and settling into the job.',
      'The service was described as white-gloved treatment.',
    ],
    quote:
      'The SearchJob team is very competent and well staffed to handle any surge in hiring needs. They have unique technology to match the right candidates with the job requirements. They provide white-gloved treatment and constantly maintain good communication with the candidate until they join and settle in the job.',
    attribution: 'Founder & CEO, Grand Gaming',
    why: {
      title: 'Scalability, candidate matching and candidate experience.',
      body: 'The public customer statement supports a story around scalability, candidate matching and candidate experience rather than a claim about a specific percentage improvement.',
      points: [
        { label: 'Scalable hiring', body: 'SearchJob.ai was described as well staffed to handle a surge in hiring needs.', icon: 'target' },
        { label: 'Better candidate matching', body: 'Technology was highlighted for matching candidates with job requirements.', icon: 'users' },
        { label: 'Consistent candidate communication', body: 'Communication continued until candidates join and settle in the job.', icon: 'heart' },
      ],
    },
  },
  kinzy: {
    slug: 'kinzy-club',
    customer: 'Kinzy Club',
    bookingPath: BOOKING_PATH,
    seo: {
      title: 'Kinzy Club case study — SearchJob.ai',
      description: 'From finding talent to finding the right people for the culture.',
      canonical: 'https://searchjob.ai/case-studies/kinzy-club',
      ogImage: 'https://searchjob.ai/og-image.png',
    },
    category: 'Consumer / Brand / Growing company',
    focus: 'Skills + attitude + culture',
    headline: 'From finding talent to finding the right people for the culture.',
    context:
      'SearchJob.ai helped Kinzy Club combine recruitment expertise with a more structured, technology-enabled candidate evaluation process.',
    assets: {
      logo: '/kinzy.png',
      quotePhoto: '/kinzy-voice-placeholder.jpg',
      logoClassName: 'h-16 w-auto object-contain sm:h-20',
      carouselLogoPlate: 'h-64',
      carouselLogoClass: 'h-52 w-auto max-w-none object-contain',
      proofLogoClassName: '',
      heroScreenshot: null,
      workflowScreenshot: null,
      evidenceScreenshot: null,
    },
    glanceCompact: true,
    glance: [
      ['Customer', 'Kinzy Club'],
      ['Focus', 'Skills + attitude + culture'],
      ['Proof', 'Customer testimonial'],
    ],
    challenge: {
      title: 'Growing without compromising candidate capability or culture.',
      body: 'As Kinzy Club grew, the hiring challenge was to move quickly while maintaining alignment on capability, attitude and culture.',
      chips: ['Capability', 'Attitude', 'Culture'],
      snapshot: [
        { label: 'Scale', body: 'Rapid team growth', icon: 'scale' },
        { label: 'Speed', body: 'Faster hiring timelines', icon: 'speed' },
        { label: 'Quality', body: 'Maintain capability & culture', icon: 'quality' },
      ],
      oldWorkflow:
        'Growth requirement → Candidate capability needed evaluation → Culture / attitude alignment mattered',
    },
    beforeSteps: [
      'Growth requirement',
      'Candidate capability needed evaluation',
      'Culture / attitude alignment mattered',
    ],
    beforeBalance: [
      { label: 'Growth', body: 'Scale hiring as the company grew' },
      { label: 'Candidate capability', body: 'Evaluate whether candidates could perform the role' },
      {
        label: 'Culture & attitude',
        body: "Maintain alignment with the company's needs and culture",
      },
    ],
    painPoints: [],
    approach: {
      title: 'Recruitment support with candidate evaluation',
      body: "SearchJob.ai combined recruitment support with structured candidate evaluation—looking beyond role capability to assess alignment with the company's needs, working style, and culture.",
      items: [],
      cards: [
        {
          title: 'Role capability',
          body: 'Assessing skills and experience for the role.',
          icon: 'target',
        },
        {
          title: 'Attitude & working style',
          body: 'Understanding how candidates work and collaborate.',
          icon: 'heart',
        },
        {
          title: 'Culture alignment',
          body: "Evaluating fit with the company's values and environment.",
          icon: 'users',
        },
      ],
    },
    workflowTitle: 'Role understanding to hire',
    workflowIntro:
      'AI-led evaluation across skills, attitude and culture, combined with human judgment before hire.',
    workflow: [
      'Role understanding',
      'Sourcing',
      'Structured screening',
      'Assessment / interview',
      'Human review',
      'Hired',
    ],
    workflowNote: {
      label: 'Skills + attitude + culture',
      body: '',
    },
    focusPoints: [
      'Skills + attitude + culture',
      'Recruitment support',
      'Candidate evaluation',
      'Structured screening / assessment',
    ],
    approvalStatus: 'draft',
    illustrativeProfile: {
      name: 'Priya Sharma',
      initials: 'PS',
      role: 'People & Culture Professional',
      experience: '5+ years',
      status: 'Strong Cultural Fit',
      tabs: ['Overview', 'Skills', 'Assessments', 'Interviews', 'Feedback'],
      activeTab: 'Overview',
      skills: [
        { label: 'Recruitment Strategy', value: '85%', percent: 85 },
        { label: 'Stakeholder Management', value: '80%', percent: 80 },
        { label: 'Team Collaboration', value: '78%', percent: 78 },
        { label: 'People Operations', value: '72%', percent: 72 },
      ],
      attitude: [
        { label: 'Ownership', value: '4.6/5', percent: 92 },
        { label: 'Communication', value: '4.4/5', percent: 88 },
        { label: 'Problem Solving', value: '4.2/5', percent: 84 },
        { label: 'Adaptability', value: '4.3/5', percent: 86 },
      ],
      culture: [
        { label: 'Values Match', value: '4.5/5', percent: 90 },
        { label: 'Work Style', value: '4.3/5', percent: 86 },
        { label: 'Team Fit', value: '4.2/5', percent: 84 },
        { label: 'Growth Mindset', value: '4.4/5', percent: 88 },
      ],
      assessments: [
        { label: 'Resume Screening', status: 'Passed' },
        { label: 'Personality Assessment', status: 'Completed' },
        { label: 'Communication Assessment', status: 'Completed' },
        { label: 'AI Interview', status: 'Completed' },
      ],
    },
    productProofTitle: 'The SearchJob.ai technology behind the workflow',
    productProof: [
      {
        title: 'Candidate profile',
        body: 'What is already known from the candidate’s background.',
        imageKey: 'heroScreenshot',
        rows: [
          { label: 'Experience', icon: 'briefcase' },
          { label: 'Qualifications', icon: 'qualification' },
          { label: 'Role relevance', icon: 'target' },
        ],
      },
      {
        title: 'Skills, attitude and culture',
        body: 'Evaluation across the key dimensions highlighted in the customer story.',
        imageKey: 'workflowScreenshot',
        kind: 'intelligence',
        rows: [
          { label: 'Skills', icon: 'target' },
          { label: 'Attitude', icon: 'heart' },
          { label: 'Culture', icon: 'users' },
        ],
      },
      {
        title: 'Screening and assessment',
        body: 'Structured screening, assessment and interview.',
        imageKey: 'evidenceScreenshot',
        rows: [
          { label: 'Structured screening', icon: 'clipboard' },
          { label: 'Assessment', icon: 'check' },
          { label: 'Interview', icon: 'users' },
        ],
      },
    ],
    metrics: [
      { label: 'Roles Handled', value: '8', verified: false, isDemo: true },
      { label: 'Candidates Processed', value: '420', verified: false, isDemo: true },
      { label: 'To Qualified Shortlist', value: '6 Days', verified: false, isDemo: true },
    ],
    resultsTitle: 'Customer-backed outcomes',
    resultsIntro: 'Measurable hiring impact, supported by customer feedback.',
    results: [
      "SearchJob.ai understood Kinzy Club's unique culture.",
      'Candidates were sourced with the right skills and the right attitude.',
      'Attention to detail and commitment were praised.',
      'The customer described the service as a game-changer for a growing brand.',
    ],
    resultEvidence: [
      { label: 'Culture fit', body: "SearchJob.ai understood Kinzy Club's unique culture.", kind: 'check' },
      { label: 'Sourcing quality', body: 'Candidates had the right skills and attitude.', kind: 'check' },
      { label: 'Delivery', body: 'Attention to detail and commitment were praised.', kind: 'check' },
    ],
    quote:
      'Working with SearchJob.ai has been a game-changer for us at Kinzy Club. As a growing brand, finding the right talent quickly and effectively is crucial — and their team delivered beyond expectations. From understanding our unique culture to sourcing candidates who not only had the right skills but also the right attitude, their attention to detail and commitment were unmatched.',
    attribution: 'Yash Maheswari, Co-Founder, Kinzy Club',
    quoteBy: 'Yash Maheswari',
    quoteRole: 'Co-Founder, Kinzy Club',
    why: {
      title: 'Skills, attitude and culture.',
      body: 'The public customer testimony supports a candidate-quality and experience story, particularly around skills, attitude and culture.',
      points: [
        { label: 'Skills', body: 'Candidates with the right technical and role-specific skills.', icon: 'target' },
        { label: 'Attitude', body: 'A proactive mindset and strong work ethic.', icon: 'heart' },
        { label: 'Culture', body: "A strong fit with the team's values and way of working.", icon: 'users' },
      ],
    },
  },
};

export function assetSrc(study, imageKey) {
  if (!imageKey || !study.assets) return null;
  const value = study.assets[imageKey];
  return typeof value === 'string' && value.length > 0 ? value : null;
}
